import React from 'react';
import { useNavigate } from 'react-router-dom';

import { CloneMode } from '../../services/admin/BackendService';
import { CommonLoggedInDataContext } from '../../models/CommonLoggedInData';
import useRecentlyViewed from '../../hooks/admin/useRecentlyViewed';
import FundStatusWorkflow from './FundOpportunityAdmin/FundStatusWorkflow';
import { FundStatusWorkflowHelper } from './FundOpportunityAdmin/useFundStatusWorkflow';
import { buildInitialState } from './FundOpportunityAdmin/buildInitialState';
import FundOpportunityLayout from './FundOpportunityAdmin/FundOpportunityLayout';
import { handleInvestorOperationWithWarning } from './FundOpportunityAdmin/investorOperationResponse';
import { createFundOpportunityLayoutHandlers } from './FundOpportunityAdmin/layoutHandlers';
import {
  addAllCandidatesForFund,
  cloneFundOpportunityRecord,
  deleteFundOpportunityRecord,
  fetchFundInvestmentData,
  parseCloneErrorMessage,
  updateFundOpportunityDetails,
  uploadIrrCsv,
} from './FundOpportunityAdmin/fundOpportunityAdminOperations';
import {
  applyHighlightAnimation,
  findHighlightedCardElement,
} from './FundOpportunityAdmin/highlightUtils';
import {
  FundOpportunityAdminProps,
  FundOpportunityAdminState,
  FundOpportunityAdminWithNavigationProps,
} from './FundOpportunityAdmin/types';

export type { FundOpportunityAdminState } from './FundOpportunityAdmin/types';

export class FundOpportunityAdminImpl extends React.Component<
  FundOpportunityAdminWithNavigationProps,
  FundOpportunityAdminState
> {
  static contextType = CommonLoggedInDataContext;
  static sendMessageIconRef = React.createRef<HTMLDivElement>();

  constructor(props: FundOpportunityAdminWithNavigationProps) {
    super(props);
    this.state = buildInitialState(props.fundOpportunity);
  }

  showToast = (
    message: string,
    type: FundOpportunityAdminState['toastType'],
    autoHideDuration?: number,
  ) => {
    this.setState({
      toastMessage: message,
      toastType: type,
      toastAutoHideDuration: autoHideDuration,
    });
  };

  clearToast = () => {
    this.setState({
      toastMessage: null,
      toastType: null,
      toastAutoHideDuration: undefined,
    });
  };

  handleAddAllCandidates = () => {
    this.setState({ isSubmitting: true });
    addAllCandidatesForFund(this.props.fundOpportunity.id)
      .then(() => {
        this.setState({ isSubmitting: false });
        this.showToast('All contacts added as candidates successfully', 'success');
        this.loadData();
      })
      .catch((error) => {
        console.error('Error adding all candidates:', error);
        this.setState({ isSubmitting: false });
        this.showToast('Error adding contacts as candidates', 'error');
      });
  };

  openCloneModal = () => {
    this.setState({ cloneModalOpen: true, cloneError: undefined });
  };

  closeCloneModal = () => {
    this.setState({
      cloneModalOpen: false,
      cloneError: undefined,
      cloneSubmitting: false,
    });
  };

  openReviewRequestModal = () => {
    console.log('Review request modal would open');
  };

  openDeleteModal = () => {
    this.setState({
      deleteModalOpen: true,
      deleteError: undefined,
      deleteWarning: undefined,
    });
  };

  closeDeleteModal = () => {
    this.setState({
      deleteModalOpen: false,
      deleteError: undefined,
      deleteWarning: undefined,
    });
  };

  handleDelete = async (force: boolean = false) => {
    this.setState({ deleteSubmitting: true, deleteError: undefined });

    try {
      await deleteFundOpportunityRecord(this.props.fundOpportunity.id, force);
      this.showToast('Fund opportunity deleted successfully', 'success');
      this.props.navigate('/');
    } catch (error: any) {
      if (error.type === 'warning') {
        this.setState({
          deleteWarning: error.data,
          deleteSubmitting: false,
        });
      } else {
        this.setState({
          deleteError: error.message || 'Failed to delete fund opportunity',
          deleteSubmitting: false,
        });
      }
    }
  };

  handleClone = (mode: CloneMode, newName: string, destinationExternalId?: string) => {
    this.setState({ cloneSubmitting: true, cloneError: undefined });

    cloneFundOpportunityRecord(this.props.fundOpportunity.id, mode, newName, destinationExternalId)
      .then((response) => {
        this.setState({ cloneSubmitting: false, cloneModalOpen: false });
        this.showToast(
          `Successfully cloned fund opportunity. [View new opportunity](/opportunities/${response.id})`,
          'success',
          20000,
        );
      })
      .catch((error) => {
        console.error('Error cloning fund opportunity:', error);
        this.setState({ cloneSubmitting: false });
        this.showToast(parseCloneErrorMessage(error), 'error');
      });
  };

  handleInvestorOperationResponse = (
    operationFn: () => Promise<any>,
    action: string,
    investor: any,
    amount?: number,
  ): Promise<void> =>
    handleInvestorOperationWithWarning(
      operationFn,
      action,
      investor,
      {
        setFundStatusWorkflow: (newState, backendWarningMessage) =>
          this.setState({
            fundStatusWorkflow: { ...this.state.fundStatusWorkflow, ...newState },
            backendWarningMessage,
          }),
        reloadData: this.loadData,
      },
      amount,
    );

  componentDidMount(): void {
    this.loadData();
    this.props.trackView('FUND_OPPORTUNITY', this.props.fundOpportunity.id);
  }

  componentDidUpdate(): void {
    if (
      this.state.loaded &&
      !this.state.loading &&
      (this.state.highlightedContactId || this.state.highlightedInvestorId) &&
      !this.state.hasScrolledToHighlight &&
      this.state.data?.investors
    ) {
      this.scrollToHighlightedCard();
    }
  }

  loadData = () => {
    const { fundOpportunity } = this.props;
    this.setState({ loading: true });
    fetchFundInvestmentData(fundOpportunity.id)
      .then((data) => {
        this.setState({
          loaded: true,
          loading: false,
          data,
          addresses: data.opportunity.addresses || [],
        });
      })
      .catch((err) => {
        console.error('Failed to load fund investment data', err);
        this.setState({ loading: false });
      });
  };

  scrollToHighlightedCard = () => {
    if (!this.state.highlightedContactId && !this.state.highlightedInvestorId) return;

    const highlightedElement = findHighlightedCardElement(
      this.state.highlightedContactId,
      this.state.highlightedInvestorId,
    );

    if (highlightedElement) {
      applyHighlightAnimation(highlightedElement);
      this.setState({ hasScrolledToHighlight: true });
    }
  };

  handleInputChange = (
    field: keyof FundOpportunityAdminState['formData'],
    value: string | number | string[] | boolean,
    callback?: () => void,
  ) => {
    this.setState(
      (prevState) => ({
        formData: {
          ...prevState.formData,
          [field]: value,
        },
      }),
      () => {
        if (callback) callback();
      },
    );
  };

  handleSectorChange = (sectorOption: FundOpportunityAdminState['formData']['sector'][number]) => {
    this.setState((prevState) => {
      const current = prevState.formData.sector || [];
      const updated = current.includes(sectorOption)
        ? current.filter((s) => s !== sectorOption)
        : [...current, sectorOption];
      return {
        formData: {
          ...prevState.formData,
          sector: updated,
        },
      };
    });
  };

  handleSubmit = () => {
    this.setState({ isSubmitting: true });
    updateFundOpportunityDetails(this.props.fundOpportunity.id, this.state.formData)
      .then(() => {
        console.log('Successfully updated fund opportunity.');
        this.setState({ isSubmitting: false });
      })
      .catch((error) => {
        console.error('Error updating fund opportunity:', error);
        this.setState({ isSubmitting: false });
      });
  };

  handleCsvUpload = (file: File) => {
    if (!file) return;

    this.setState({ isSubmitting: true });
    uploadIrrCsv(this.props.fundOpportunity.id, file)
      .then(() => {
        this.setState({ isSubmitting: false });
        this.showToast('CSV uploaded and processed successfully', 'success');
      })
      .catch((error) => {
        console.error('Error uploading CSV', error);
        this.setState({ isSubmitting: false });
        this.showToast('Error uploading or processing CSV', 'error');
      });
  };

  collectInvestment(label: string, initialValue?: number): Promise<number> {
    return new Promise((resolve) => {
      this.setState({
        investmentPromptOpen: true,
        investmentPromptLabel: label,
        investmentPromptValue: initialValue?.toString() || '',
        investmentPromptResolver: resolve,
      });
    });
  }

  handleFundStatusClose = () => {
    FundStatusWorkflowHelper.closeFundStatusConfirmModal((newState) =>
      this.setState({
        fundStatusWorkflow: { ...this.state.fundStatusWorkflow, ...newState },
        backendWarningMessage: undefined,
      }),
    );
  };

  handleFundStatusConfirm = () => {
    FundStatusWorkflowHelper.handleFundStatusConfirm(this.state.fundStatusWorkflow, (newState) =>
      this.setState({
        fundStatusWorkflow: { ...this.state.fundStatusWorkflow, ...newState },
        backendWarningMessage: undefined,
      }),
    );
  };

  renderFundStatusWorkflow() {
    const { fundStatusWorkflow } = this.state;

    return (
      <FundStatusWorkflow
        fundOpportunity={this.props.fundOpportunity}
        open={fundStatusWorkflow.fundStatusConfirmModalOpen}
        action={fundStatusWorkflow.fundStatusConfirmAction}
        investor={fundStatusWorkflow.fundStatusConfirmInvestor}
        amount={fundStatusWorkflow.fundStatusConfirmAmount}
        onClose={this.handleFundStatusClose}
        onConfirm={this.handleFundStatusConfirm}
      />
    );
  }

  render() {
    return (
      <FundOpportunityLayout
        fundOpportunity={this.props.fundOpportunity}
        hasPermission={this.props.hasPermission}
        sx={this.props.sx}
        state={this.state}
        handlers={createFundOpportunityLayoutHandlers(this)}
      />
    );
  }
}

const FundOpportunityAdminWithNavigation = (props: FundOpportunityAdminProps) => {
  const navigate = useNavigate();
  const { trackView } = useRecentlyViewed();
  return <FundOpportunityAdminImpl {...props} navigate={navigate} trackView={trackView} />;
};

export default FundOpportunityAdminWithNavigation;
